import type {
  Answer,
  ChooseNextStep,
  FunnelConfig,
  FunnelState,
  Step,
  StepType,
} from "@funnel-engine/types";

/**
 * `@nextday/funnel-engine` — pure-core state machine for the onboarding funnel.
 *
 * v0 scope (Phase 1): linear walk over `config.steps`, no branching.
 * Phase 3 (D-39): branching via the optional `chooseNextStep` adapter.
 *
 * All operations are pure functions — they consume `FunnelState` and return a new
 * `FunnelState`. Persistence (localStorage, D1) and React glue live in callers.
 */

/**
 * Invisible step types render nothing and advance on mount. They do emit view
 * and completion events, but are excluded from visible sequences and numbering
 * because they represent no user decision.
 */
const INVISIBLE_AUTO_ADVANCING_STEP_TYPE_VALUES: ReadonlyArray<StepType> = [
  "value_map",
  "handoff_config",
  "user_sync",
];

export const INVISIBLE_AUTO_ADVANCING_STEP_TYPES: ReadonlySet<string> = new Set(
  INVISIBLE_AUTO_ADVANCING_STEP_TYPE_VALUES,
);

const findStepIndex = (config: FunnelConfig, stepId: string): number =>
  config.steps.findIndex((step) => step.id === stepId);

const requireFirstStep = (config: FunnelConfig): Step => {
  const first = config.steps[0];
  if (!first) {
    throw new Error(`@nextday/funnel-engine: funnel "${config.id}" has no steps`);
  }
  return first;
};

/** Build the initial state for a fresh visit. Points at the first step. */
export const createFunnelState = (config: FunnelConfig): FunnelState => {
  const first = requireFirstStep(config);
  return {
    funnelId: config.id,
    overrideId: config.overrideId,
    history: [first.id],
    answers: [],
    completed: false,
  };
};

/** Resolve the step the user is currently on. Returns `null` if completed. */
export const getCurrentStep = (state: FunnelState, config: FunnelConfig): Step | null => {
  if (state.completed) return null;
  const currentId = state.history[state.history.length - 1];
  if (!currentId) return null;
  return config.steps.find((step) => step.id === currentId) ?? null;
};

/** Has the user already answered a given step? */
export const getAnswerForStep = (state: FunnelState, stepId: string): Answer | undefined =>
  state.answers.find((answer) => answer.stepId === stepId);

const recordAnswer = (answers: ReadonlyArray<Answer>, newAnswer: Answer): ReadonlyArray<Answer> => {
  const without = answers.filter((existing) => existing.stepId !== newAnswer.stepId);
  return [...without, newAnswer];
};

const resolveNextStepId = (params: {
  current: Step;
  state: FunnelState;
  config: FunnelConfig;
  latestAnswer?: Answer;
  chooseNextStep?: ChooseNextStep;
}): string | null => {
  const { current, state, config, latestAnswer, chooseNextStep } = params;

  if (chooseNextStep) {
    const chosen = chooseNextStep({ current, state, config, latestAnswer });
    if (chosen !== null) return chosen;
  }

  // Linear fallback (v0 default).
  const currentIdx = findStepIndex(config, current.id);
  const next = config.steps[currentIdx + 1];
  return next ? next.id : null;
};

/**
 * Advance to the next step. Optionally records an `answer` for the current step.
 *
 * - If `chooseNextStep` returns a step ID, jump there.
 * - Otherwise, walk linearly. Falling off the end marks the funnel `completed`.
 */
export const advance = (
  state: FunnelState,
  config: FunnelConfig,
  options: {
    answer?: { value: unknown; answeredAt?: number };
    chooseNextStep?: ChooseNextStep;
  } = {},
): FunnelState => {
  const current = getCurrentStep(state, config);
  if (!current) return state;

  const latestAnswer: Answer | undefined = options.answer
    ? {
        stepId: current.id,
        value: options.answer.value,
        answeredAt: options.answer.answeredAt ?? Date.now(),
      }
    : undefined;

  const answers = latestAnswer ? recordAnswer(state.answers, latestAnswer) : state.answers;

  // returnTo wins over linear walk: when set (typically by an edit-pill goTo),
  // jump back to the launch point instead of re-walking the funnel from here.
  // Bugbot NX-9280: edit pills used to push the target step, then advance walked
  // linearly through every subsequent step before reaching review again.
  if (state.returnTo && findStepIndex(config, state.returnTo) !== -1) {
    return {
      ...state,
      answers,
      history: [...state.history, state.returnTo],
      returnTo: undefined,
    };
  }

  const nextStepId = resolveNextStepId({
    current,
    state,
    config,
    latestAnswer,
    chooseNextStep: options.chooseNextStep,
  });

  if (nextStepId === null) {
    return { ...state, answers, completed: true };
  }

  return { ...state, answers, history: [...state.history, nextStepId] };
};

/**
 * Pop the most recent step off `history`. No-op if already at the first step.
 *
 * Special case: if the funnel is `completed`, just clear the flag — the
 * current step (last entry in `history`) is already the final non-terminal step.
 *
 * When `config` is provided, invisible auto-advancing steps are skipped so the
 * user lands on the previous interactive step instead of being immediately
 * bounced forward again.
 */
export const goBack = (state: FunnelState, config?: FunnelConfig): FunnelState => {
  if (state.completed) return { ...state, completed: false };
  if (state.history.length <= 1) return state;
  let nextHistory = state.history.slice(0, -1);

  if (config) {
    while (nextHistory.length > 1) {
      const prevId = nextHistory[nextHistory.length - 1];
      const prevStep = prevId ? config.steps.find((s) => s.id === prevId) : undefined;
      if (prevStep && INVISIBLE_AUTO_ADVANCING_STEP_TYPES.has(prevStep.type)) {
        nextHistory = nextHistory.slice(0, -1);
      } else {
        break;
      }
    }
  }

  return { ...state, history: nextHistory, returnTo: undefined };
};

/**
 * Jump directly to a named step, pushing it onto `history`. Used by the
 * `AttributeReview` "tap pill to edit" UX — the user lands on the target
 * step, and `goBack` will return them to the page they jumped from.
 *
 * No-op if the step ID isn't part of the config. Clears `completed` when
 * navigating away from a finished funnel.
 */
export const goTo = (
  state: FunnelState,
  config: FunnelConfig,
  stepId: string,
  options: { returnTo?: string } = {},
): FunnelState => {
  if (findStepIndex(config, stepId) === -1) return state;
  return {
    ...state,
    history: [...state.history, stepId],
    completed: false,
    returnTo: options.returnTo,
  };
};

/** Reset to the initial state — used by `Restart funnel` controls in admin previews. */
export const restart = (config: FunnelConfig): FunnelState => createFunnelState(config);

/** Whether a step should be excluded from the progress count. Invisible
 *  auto-advancing types are always excluded, even without an explicit
 *  `advances_progress: false` flag. */
const isUncounted = (step: Step): boolean =>
  step.advances_progress === false || INVISIBLE_AUTO_ADVANCING_STEP_TYPES.has(step.type);

/**
 * 1-based position of `stepId` among the counted steps, or `undefined` when
 * the step is uncounted or unknown.
 *
 * Shares `isUncounted` with `getProgress`, so the progress indicator and the
 * analytics `step_index` cannot drift into separate definitions of "counted".
 */
export const countedStepPosition = (config: FunnelConfig, stepId: string): number | undefined => {
  const idx = config.steps.filter((step) => !isUncounted(step)).findIndex((s) => s.id === stepId);
  return idx >= 0 ? idx + 1 : undefined;
};

/**
 * Position of the current step in the progress indicator.
 *
 * Counts only steps that are interactive AND have `advances_progress !== false`.
 * Invisible auto-advancing types are always excluded, even without an explicit
 * flag.
 *
 * When the current step is itself uncounted, return the count of counted steps
 * already traversed in `history` so the bar holds at the previous mark instead
 * of snapping back to 0 between counted steps (Bugbot NX-9280). Consumers
 * driving "show login until index < 3" therefore see a monotonic value through
 * the funnel.
 */
export const getProgress = (
  state: FunnelState,
  config: FunnelConfig,
): { index: number; total: number } => {
  const counted = config.steps.filter((step) => !isUncounted(step));
  const total = counted.length;
  if (state.completed) return { index: total, total };

  // Walk history from most recent backward, returning the position of the
  // first counted step found. This naturally handles:
  //  - current step is counted → returns its index + 1
  //  - current step is interstitial → holds at the previous counted step
  //  - history contains duplicates (e.g. AttributeReview edit-pill jumps) →
  //    cannot exceed `total` because we use the step's index in `counted`,
  //    not a count of visits. Bugbot NX-9280.
  for (let i = state.history.length - 1; i >= 0; i--) {
    const idx = counted.findIndex((step) => step.id === state.history[i]);
    if (idx >= 0) return { index: idx + 1, total };
  }
  return { index: 0, total };
};

export type {
  Answer,
  ChooseNextStep,
  FunnelConfig,
  FunnelState,
  Step,
  StepType,
} from "@funnel-engine/types";
